/* eslint-disable max-classes-per-file */
import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import camelCaseKeys from 'camelcase-keys';
import snakeCaseKeys from 'snakecase-keys';
import { v4 } from 'uuid';

import RoutePath from '@custom-types/RoutePath';
import { generateRoute } from '@services/navigation-service';
import { getKioskEnabled } from '@state/kiosk';
import store from '@state/store';

const ERR_NETWORK = 'E_NETWORK';
const ERR_UNKNOWN_SERVER_EXCEPTION = 'E_UNKNOWN_SERVER_EXCEPTION';

class RequestError extends Error {
  private readonly headers: AxiosRequestHeaders | undefined;

  private readonly type: string;

  constructor(message: string, request: AxiosRequestConfig) {
    super();

    this.message = message;
    this.stack = new Error().stack;
    this.name = this.constructor.name;

    this.headers = request.headers;

    this.type = this.message === 'Network Error' ? ERR_NETWORK : ERR_UNKNOWN_SERVER_EXCEPTION;
  }
}

class ResponseError extends Error {
  private readonly status: number;

  private readonly statusText: string;

  private readonly headers: Record<string, string> & { 'set-cookie'?: string[] };

  // skipcq: JS-0323
  private readonly errors: any;

  // skipcq: JS-0323
  private readonly types: any;

  private readonly type: string;

  constructor(message: string, response: AxiosResponse) {
    super();

    this.message = message;
    this.stack = new Error().stack;
    this.name = this.constructor.name;

    const { status, statusText, headers } = response;

    this.status = status;
    this.statusText = statusText;
    this.headers = headers;

    this.errors = response?.data?.errors ?? [];
    this.types = this.errors.map(({ code }: { code: any }) => code);

    this.type =
      this.types?.[0] ??
      (this.message === 'Network Error' ? ERR_NETWORK : ERR_UNKNOWN_SERVER_EXCEPTION);
  }
}

const DEFAULT_HEADERS = {
  Accept: 'application/vnd.eventmobi+json; version=p.6',
  'X-Request-GUID': v4(),
};

export default function apiFn(
  baseURL: string,
  headers: AxiosRequestHeaders = {},
  withCredentials = true,
  options: Omit<AxiosRequestConfig, 'baseURL' | 'headers' | 'withCredentials'> = {}
) {
  const api = axios.create({
    baseURL,
    headers: { ...DEFAULT_HEADERS, ...headers },
    withCredentials,
    ...options,
  });

  api.interceptors.request.use(
    request => {
      // anything that needs to be done before request is sent
      // can be done here
      if (request.data) {
        Object.assign(request, {
          data: snakeCaseKeys(request.data, { deep: true }),
        });
      }
      if (request.params) {
        Object.assign(request, {
          params: snakeCaseKeys(request.params, { deep: true }),
        });
      }
      return request;
    },
    error => Promise.reject(new RequestError(error.message, error.request))
  );

  // Add a response interceptor
  api.interceptors.response.use(
    response =>
      // anything that needs to be done before response is returned
      // can be done here
      Object.assign(response, {
        data: camelCaseKeys(response.data, { deep: true }),
      }),
    error => {
      const responseURL = error?.request?.responseURL ?? '';
      const isAuthRequest = responseURL.includes('api/uapi/onsite-app/login');

      if (!isAuthRequest) {
        const currentPath = `${window.location.pathname}${encodeURIComponent(
          window.location.search
        )}`;
        if (error?.response?.status === 401) {
          const isKioskEnabled = getKioskEnabled(store.getState());
          if (!isKioskEnabled) {
            window.location.replace(generateRoute(`${RoutePath.Login}?next=${currentPath}`));
          }
        }
      }

      return Promise.reject(new ResponseError(error.message, error.response));
    }
  );

  return api;
}

/* eslint-enable max-classes-per-file */
